import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import StaffManagement from '../components/StaffManagement';
import { scenarioService, Scenario } from '../services/scenarioService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [originalScenarios, setOriginalScenarios] = useState<Scenario[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadScenarios = async () => {
      setLoading(true);
      try {
        // Try to migrate from localStorage first
        await scenarioService.migrateFromLocalStorage();

        // Load scenarios from API
        const loadedScenarios = await scenarioService.getScenarios();
        setScenarios(loadedScenarios);
        setOriginalScenarios(JSON.parse(JSON.stringify(loadedScenarios))); // Deep copy
      } catch (error) {
        console.error('Error loading scenarios:', error);
      } finally {
        setLoading(false);
      }
    };

    loadScenarios();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePromptChange = (scenarioId: string, value: string) => {
    setScenarios(prev => prev.map(scenario =>
      scenario.id === scenarioId
        ? { ...scenario, prompt_template: value }
        : scenario
    ));
  };

  const handleSmsIntroChange = (scenarioId: string, value: string) => {
    setScenarios(prev => prev.map(scenario =>
      scenario.id === scenarioId
        ? { ...scenario, sms_intro_template: value }
        : scenario
    ));
  };

  const handleSmsContextChange = (scenarioId: string, value: string) => {
    setScenarios(prev => prev.map(scenario =>
      scenario.id === scenarioId
        ? { ...scenario, sms_context_prompt: value }
        : scenario
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update changed scenarios
      const promises = scenarios.map(async (scenario) => {
        const original = originalScenarios.find(o => o.id === scenario.id);
        if (original && original.prompt_template !== scenario.prompt_template) {
          const originalIntro = original.sms_intro_template ?? '';
          const currentIntro = scenario.sms_intro_template ?? '';
          const originalContext = original.sms_context_prompt ?? '';
          const currentContext = scenario.sms_context_prompt ?? '';

          if (
            original.prompt_template !== scenario.prompt_template ||
            originalIntro !== currentIntro ||
            originalContext !== currentContext
          ) {
            return scenarioService.updateScenario(scenario.id, {
              name: scenario.name,
              description: scenario.description,
              prompt_template: scenario.prompt_template,
              variables: scenario.variables,
              category: scenario.category,
              is_active: scenario.is_active,
              sms_intro_template: scenario.sms_intro_template ?? null,
              sms_context_prompt: scenario.sms_context_prompt ?? null,
            });
          }
          return null;
        }
        return null;
      });

      await Promise.all(promises);

      // Update originalScenarios to reflect saved state
      setOriginalScenarios(JSON.parse(JSON.stringify(scenarios)));
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving scenarios:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (scenarioId: string) => {
    const original = originalScenarios.find(s => s.id === scenarioId);
    if (original) {
      setScenarios(prev => prev.map(scenario =>
        scenario.id === scenarioId
          ? { ...scenario, prompt_template: original.prompt_template }
          : scenario
      ));
    }
  };

  const handleResetAll = () => {
    setScenarios(JSON.parse(JSON.stringify(originalScenarios)));
  };

  const handleCopyPrompt = (prompt?: string | null) => {
    if (!prompt) {
      return;
    }
    navigator.clipboard.writeText(prompt);
  };

  const hasChanges = JSON.stringify(scenarios) !== JSON.stringify(originalScenarios);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize your simulator prompts and conversation templates
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
            <Tab label="Simulator Prompts" />
            <Tab label="SMS Prompts" />
            <Tab label="Staff Management" />
            <Tab label="General Settings" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Customize the initial greeting messages for each scenario. Use variables like {'{name}'}, {'{vehicle}'}, {'{mileage}'} to personalize messages.
              </Alert>
            </Grid>

            {loading ? (
              <Grid item xs={12}>
                <Alert severity="info">Loading scenarios...</Alert>
              </Grid>
            ) : (
              scenarios.map((scenario) => (
                <Grid item xs={12} key={scenario.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {scenario.name}
                            {scenario.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {scenario.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            {scenario.variables.map(variable => (
                              <Chip
                                key={variable}
                                label={`{${variable}}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Copy prompt">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyPrompt(scenario.prompt_template)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset to saved">
                            <IconButton
                              size="small"
                              onClick={() => handleReset(scenario.id)}
                            >
                              <ResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        value={scenario.prompt_template}
                        onChange={(e) => handlePromptChange(scenario.id, e.target.value)}
                        placeholder="Enter your custom prompt..."
                        sx={{
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={handleResetAll}
                  disabled={loading}
                >
                  Reset All to Saved
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasChanges || saving || loading}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Define the first real SMS and assistant instructions for each scenario. Placeholders like {'{name}'}, {'{vehicle}'}, and {'{phone_number}'} will be replaced automatically.
              </Alert>
            </Grid>

            {loading ? (
              <Grid item xs={12}>
                <Alert severity="info">Loading scenarios...</Alert>
              </Grid>
            ) : (
              scenarios.map((scenario) => (
                <Grid item xs={12} key={`${scenario.id}-sms`}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {scenario.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Configure the SMS sent to customers and the AI instructions used to craft follow-ups.
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Copy SMS template">
                            <IconButton size="small" onClick={() => handleCopyPrompt(scenario.sms_intro_template)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy assistant context">
                            <IconButton size="small" onClick={() => handleCopyPrompt(scenario.sms_context_prompt)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset to saved">
                            <IconButton size="small" onClick={() => handleReset(scenario.id)}>
                              <ResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <TextField
                        fullWidth
                        label="Initial SMS Template"
                        multiline
                        rows={3}
                        variant="outlined"
                        value={scenario.sms_intro_template ?? ''}
                        onChange={(e) => handleSmsIntroChange(scenario.id, e.target.value)}
                        placeholder="Hi {name}, your {vehicle} is ..."
                        sx={{
                          mb: 3,
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                          },
                        }}
                        helperText="Keep under 160 characters. Placeholders will be replaced automatically."
                      />

                      <TextField
                        fullWidth
                        label="Assistant Context Prompt"
                        multiline
                        rows={6}
                        variant="outlined"
                        value={scenario.sms_context_prompt ?? ''}
                        onChange={(e) => handleSmsContextChange(scenario.id, e.target.value)}
                        placeholder="You are sending a reminder to {name} about their {vehicle}..."
                        sx={{
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                          },
                        }}
                        helperText="These instructions are passed to the AI assistant before it drafts the SMS."
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={handleResetAll}
                  disabled={loading}
                >
                  Reset All to Saved
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasChanges || saving || loading}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <StaffManagement />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Additional configuration options coming soon...
          </Typography>
        </TabPanel>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
